import React, { useEffect, useState } from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { useSearchParams } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AaiRequest, AaiSkill } from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, FilterChips, ListRow, Loading, PageBody, PageHead, Panel, PanelHead, RouteMark,
  StatusChip, Tabs,
} from '../../components/aa';
import { useRecordLinks } from '../../hooks/useRecordLinks';
import { audienceOf, brief, isReadyToUse } from '../owner/WorkRows';
import { nextFor, ownerOf, stateFor } from '../partner/work';
import { relativeTime } from '../partner/format';
import { isOpen, needsChampion, plural } from './champion';

/*
 * Projects, for the AI Champion: every approved piece of company work. Quick
 * Wins are the published capabilities, used and reviewed in the portal.
 * Delivery Projects open the Project record, where the champion's decision on
 * a cleared request lives; a Workspace opens from there for named members only.
 */

type WorkFilter = 'active' | 'needs_me' | 'in_progress' | 'waiting' | 'ready' | 'all';
const FILTERS: Array<{ id: WorkFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'needs_me', label: 'Needs me' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'ready', label: 'Ready to use' },
  { id: 'all', label: 'All' },
];

const MATCH: Record<WorkFilter, (r: AaiRequest) => boolean> = {
  active: isOpen,
  needs_me: needsChampion,
  in_progress: (r) => !isReadyToUse(r) && r.status === 'approved',
  waiting: (r) => r.status === 'pending' || r.status === 'needs_info',
  ready: isReadyToUse,
  all: () => true,
};

const stateWord = (r: AaiRequest): string => {
  if (isReadyToUse(r)) return 'Ready to use';
  if (r.status === 'approved' && r.build_state === 'tested') return 'Tested · publish the tool';
  if (r.status === 'approved' && r.build_state === 'built') return 'Built · publish the tool';
  if (r.status === 'approved') return 'In build';
  if (r.status === 'dept_approved') return 'Cleared by the department head · your decision';
  if (r.status === 'needs_info') return 'Detail requested';
  if (r.status === 'declined') return 'Declined';
  if (!r.department || r.reviewer_gate === 'company') return 'No department to clear it · your decision';
  return 'Waiting on the department head';
};

const DeliveryRow: React.FC<{ request: AaiRequest; onOpen: (id: string) => void }> = ({ request, onOpen }) => {
  let status = stateFor(request);
  if (isReadyToUse(request)) status = 'ready_short';
  let when = `Raised ${relativeTime(request.created_at)}`;
  if (request.decided_at) when = `Decided ${relativeTime(request.decided_at)}`;
  return (
    <ListRow
      mark={<span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-[13px] font-bold text-white">D</span>}
      title={request.title}
      badge={(
        <>
          <StatusChip status={status} />
          {needsChampion(request) && <Chip tone="warn" dot>Needs you</Chip>}
        </>
      )}
      desc={brief(request.detail)}
      foot={`${stateWord(request)} · ${when}`}
      meta={[
        { label: 'Owner', value: ownerOf(request), foot: request.department || 'Whole company' },
        { label: 'Next', value: nextFor(request) },
      ]}
      onClick={() => onOpen(request.id)}
      arrow
    />
  );
};

const QuickWinRow: React.FC<{ skill: AaiSkill; onOpen: (skill: AaiSkill) => void }> = ({ skill, onOpen }) => {
  let setup = 'Published';
  if (skill.use_case_id) setup = 'Already approved';
  let scope = 'Company';
  if (skill.access_level === 'restricted') scope = 'Named people';
  return (
    <ListRow
      mark={<RouteMark kind="quick_win" size="lg" />}
      title={skill.name}
      desc={brief(skill.problem)}
      meta={[
        { label: 'Scope', value: scope },
        { label: 'For', value: audienceOf(skill) },
        { label: 'Setup', value: setup },
      ]}
      right={<StatusChip status="ready_short" />}
      onClick={() => onOpen(skill)}
      arrow
    />
  );
};

const ChampionProjectsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'all';
  const openParam = params.get('open');
  const requests = useAsync<{ scope: string; requests: AaiRequest[] }>(() => aaiApi.listRequests(), 'Could not load company work.');
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load Quick Wins.');
  const [filter, setFilter] = useState<WorkFilter>('active');
  const links = useRecordLinks();
  const setOpenProject = links.project;
  const setOpenSkill = (skill: AaiSkill) => links.quickWin(skill.id);

  useEffect(() => {
    if (openParam) setOpenProject(openParam, true);
  }, [openParam, setOpenProject]);

  const setTab = (id: string) => setParams({ tab: id }, { replace: true });

  const all = requests.data?.requests || [];
  const delivery = all.filter((r) => !isReadyToUse(r));
  const rows = delivery.filter(MATCH[filter]);
  const quickWins = (skills.data?.skills || []).filter((s) => s.published);
  const needsMe = all.filter(needsChampion).length;
  const showQuick = tab === 'all' || tab === 'quick_wins';
  const showDelivery = tab === 'all' || tab === 'delivery';

  return (
    <PageBody>
      <PageHead
        align="center"
        title="Projects"
        actions={askOrbitAction()}
      />

      <Tabs
        tabs={[
          { id: 'all', label: 'All projects', count: quickWins.length + delivery.filter(isOpen).length },
          { id: 'quick_wins', label: 'Quick Wins', count: quickWins.length },
          { id: 'delivery', label: 'Delivery Projects', count: delivery.filter(isOpen).length },
        ]}
        active={tab}
        onChange={setTab}
        label="Project type"
      />

      {showQuick && (
        <Panel className="mb-6">
          <PanelHead
            kicker="Project type · Quick Win"
            title="Focused projects ready to use"
            desc="A focused result with limited setup, used and reviewed directly in the portal."
            action={tab === 'all' && <Action size="sm" variant="quiet" onClick={() => setTab('quick_wins')}>See all Quick Wins</Action>}
          />
          {skills.loading && <Loading label="Loading Quick Wins" />}
          {skills.error && <div className="px-6 py-5"><ErrorNote message={skills.error} /></div>}
          {!skills.loading && !skills.error && quickWins.length === 0 && (
            <EmptyState title="No Quick Wins yet" reason="A Quick Win appears here once a capability is published for the company or a team." />
          )}
          <Divided>
            {quickWins.map((s) => <QuickWinRow key={s.id} skill={s} onOpen={setOpenSkill} />)}
          </Divided>
        </Panel>
      )}

      {showDelivery && (
        <>
          <div className="mb-3.5 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Project type · Delivery Project</div>
              <h2 className="m-0 text-[20px] font-bold leading-[28px] tracking-[-0.005em] text-ink">Planned, quoted, and delivered with a named team</h2>
              <p className="m-0 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">A larger change that needs shared ownership, scoped delivery, coordinated work, and governed release.</p>
            </div>
            {tab === 'all' && <Action size="sm" variant="quiet" onClick={() => setTab('delivery')}>See all Delivery Projects</Action>}
          </div>

          <Panel>
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-6">
              <FilterChips
                tabs={FILTERS.map((f) => {
                  if (f.id !== 'needs_me') return f;
                  return { ...f, count: needsMe };
                })}
                active={filter}
                onChange={(id) => setFilter(id as WorkFilter)}
                label="Work state"
              />
              <span className="text-[12px] text-ink-soft">{plural(rows.length, 'project', 'projects')} in this view</span>
            </div>
            {requests.loading && <Loading label="Loading company work" />}
            {requests.error && <div className="px-6 py-5"><ErrorNote message={requests.error} /></div>}
            {!requests.loading && !requests.error && rows.length === 0 && (
              <EmptyState title="Nothing is waiting here" reason="Change the filter. Work arrives when someone asks Orbit for something." />
            )}
            <Divided>
              {rows.map((r) => <DeliveryRow key={r.id} request={r} onOpen={setOpenProject} />)}
            </Divided>
            <div className="flex items-center gap-2.5 px-5 py-4 text-[12.5px] leading-[19px] text-ink-dim md:px-6">
              <Chip tone="private" dot>Named members only</Chip>
              Project summary, Workspace membership and source access are three separate permissions. Open Workspace explains itself in the record.
            </div>
          </Panel>
        </>
      )}

    </PageBody>
  );
};

export default ChampionProjectsPage;
